import {
  filter,
  flatten,
  join,
  map,
  pipe,
  trim,
  uniq,
} from 'ramda'

import queryString from 'query-string'
import { createSelectorCreator, defaultMemoize } from 'reselect'

import { EMPTY_ARRAY, EMPTY_OBJECT } from './constants'
import { getDateTime } from './datetime'
import { shallowEquals } from './equality'
import { defaultMemoizerOpts } from './memoizer'

export * from './color'
export * from './constants'
export * from './datetime'
export * from './equality'
export * from './timing'
export * from './dom'
export * from './hooks'
export * from './ids'
export * from './lazyWithRetries'
export * from './measurements'
export { default as memoize, shallowEqualsMemoizer } from './memoizer'
export * from './params'
export * from './strings'
export * from './styles'
export * from './types'
export * from './validation'

export const noop = () => null

export const createShallowEqualsSelector = createSelectorCreator(
  defaultMemoize,
  (left, right) => shallowEquals(left, right, defaultMemoizerOpts)
)
const notANumber = o => typeof o !== 'number'
export const errorParser = response => {
  if (typeof response === 'string' || !response) return [response ?? '']

  let responseObj = response
  if (response.error) {
    if (response.error.response) {
      responseObj = response.error.response
      if (typeof responseObj === 'string') {
        return [responseObj]
      }
    } else if (response.error.message) {
      responseObj = response.error.message
      if (typeof responseObj === 'string') {
        return [responseObj]
      }
    }
  }

  let errorMsg = EMPTY_ARRAY
  if (Array.isArray(responseObj)) {
    errorMsg = responseObj.every(str => typeof str === 'string')
      ? responseObj
      : responseObj.filter(notANumber).map(errorParser)
  }
  if (Object.keys(responseObj)?.length) {
    errorMsg = Object.values(responseObj).filter(notANumber).map(errorParser)
  }

  return errorMsg
}
/**
 * Returns api errors as string with breaks
 * @param {string|object|array} response
 */
export const parseApiErrors = pipe(errorParser, flatten, map(trim), filter(Boolean), uniq, join('\n'))

/**
 * Returns errors as trimmed string with breaks
 * @param {string|array|number} [error]
 * @returns {string}
 */
export const formatError = error => {
  if (!error || typeof error === 'string') return error ? trim(error) : ''
  if (Array.isArray(error)) return error.map(formatError).join('\n')
  return String(error)
}

const defaultFormat = {
  thousandSeparator: ',',
  suffix: '',
}
/**
 *
 * @param {number} num number to format
 * @param {number} [precision] precision for decimal numbers
 * @param {Object} [format] additional formatting options
 * @param {string} [format.thousandSeparator=,]
 * @param {string} [format.suffix]
 * @returns {string} formatted numeric string
 */
export const formattedNumber = (num, precision = 0, formatParam = EMPTY_OBJECT) => {
  if (num == null || Number.isNaN(num)) return ''
  const format = { ...defaultFormat, ...formatParam }
  if (num === parseInt(num, 10) && num < 1000) {
    return format.suffix ? [num, format.suffix].join('\u202f') : String(num)
  }
  const factor = precision && 10 ** precision
  const number = precision ? Math.round(num * factor) / factor : num
  // Only remove trailing zeroes when there IS a decimal point
  const withPrecision = number.toFixed(precision).replace(
    /\.\d+$/,
    decimals => (decimals.match(/\.[0]+$/) ? '' : decimals.replace(/[0]+$/, ''))
  )
  const [integer, decimals] = withPrecision.split('.')
  if (!format.thousandSeparator || integer.length < 4) {
    return [withPrecision, format.suffix].filter(Boolean).join(' ')
  }
  const negative = integer.startsWith('-')
  const absInteger = negative ? integer.slice(1) : integer
  const thousands = []
  for (let i = absInteger.length; i > 0; i -= 3) {
    thousands.unshift(absInteger.slice(Math.max(0, i - 3), i))
  }
  let formattedNum = [thousands.join(format.thousandSeparator), decimals].filter(Boolean).join('.')
  formattedNum = format.suffix
    ? [formattedNum, format.suffix].join('\u202f')
    : formattedNum
  return negative ? `-${formattedNum}` : formattedNum
}

/**
 * Returns reduced array based on the max number provided
 * @param {array} array
 * @param {number} max
 * @param {number} startIndex
 * @returns {array}
 */
export const downsampleToMax = (array, max, startIndex = 0) => {
  const overage = array.length / max
  const k = overage >= 2 ? 2 ** Math.round(Math.log2(overage)) : Math.round(overage)

  if (k < 2) return array
  return array.filter((_, index) => (overage > 2
    ? (startIndex + index) % k === 0
    : (startIndex + index) % k !== 0))
}

/**
 * Rounds to given number of decimal places
 * @param {number} value The number to round
 * @param {number} precision The number of decimal places to allow (if falsy, rounds to no decimal places)
 * @returns number
 */
export const roundToPrecision = (value, precision) => {
  if (!value) return 0
  if (!precision || typeof precision !== 'number') return Math.round(value)
  const factor = 10 ** precision

  return Math.round(value * factor) / factor
}

/**
 * Returns url for the room dashboard with the time range
 * @param {number} roomID
 * @param {number|null} zoneID
 * @param {array|null} entity
 * @param {ts} number
 * @returns {string}
 */
export const getRoomAnnotationURL = ({ roomID, zoneID, entity = [], ts }) => {
  const time = getDateTime(ts)
  const from = time.minus({ hours: 4 }).toISO()
  const to = time.plus({ hours: 4 }).toISO()
  const [key, value] = entity
  const query = {
    from,
    to,
    zones: zoneID,
    individualSensors: true,
    ...(key && value && { [key]: value })
  }
  return `/rooms/${roomID}?${queryString.stringify(query)}`
}

const stringables = ['string', 'number']
/**
 * Utility to get text from rendered React node
 * @param {ReactNode} node
 */
export const getNodeText = node => {
  if (stringables.includes(typeof node)) {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(getNodeText).join('')
  }
  if (typeof node === 'object' && node?.props?.children) {
    return getNodeText(node.props.children)
  }
  return ''
}
